import React from 'react';
import { useLanguage } from '../context/LanguageContext';

// React requires class components for error boundaries.
// Catches render errors in any descendant; shows a recovery UI
// instead of letting a single page crash the whole SPA.
//
// Class components cannot use hooks → fallback UI is extracted to a small
// functional component so we can call `useLanguage()` inside it.
// LanguageProvider wraps ErrorBoundary in App.js, so the hook is safe.
function ErrorFallback({ msg, onReload, onGoHome }) {
  const { t } = useLanguage();
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl border border-gray-200 dark:border-gray-700 max-w-md w-full p-6 text-center">
        <div className="text-5xl mb-3" aria-hidden="true">⚠️</div>
        <h1 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
          {t('errorBoundaryTitle') || 'Bir Şeyler Ters Gitti'}
        </h1>
        <p className="text-sm text-gray-500 dark:text-gray-400 mb-5">
          {t('errorBoundaryDesc') || 'Bu sayfa beklenmedik bir hatayla karşılaştı. Tekrar deneyebilir veya ana sayfaya dönebilirsiniz.'}
        </p>

        {process.env.NODE_ENV === 'development' && msg && (
          <pre className="text-xs text-left bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg p-3 mb-5 overflow-auto max-h-32 text-red-600 dark:text-red-400 font-mono">
            {msg}
          </pre>
        )}

        <div className="flex gap-2">
          <button
            onClick={onReload}
            className="flex-1 bg-blue-600 hover:bg-blue-700 text-white py-2.5 rounded-xl font-semibold transition-colors"
          >
            <span aria-hidden="true">🔄</span> {t('errorBoundaryRefresh') || 'Yenile'}
          </button>
          <button
            onClick={onGoHome}
            className="flex-1 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 py-2.5 rounded-xl font-semibold hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
          >
            <span aria-hidden="true">🏠</span> {t('errorBoundaryGoHome') || 'Ana Sayfa'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    // Keep logs lightweight; a real monitoring hook (Sentry) can tap in here later.
    if (typeof console !== 'undefined') {
      console.error('[ErrorBoundary]', error, info?.componentStack);
    }
  }

  handleReload = () => {
    window.location.reload();
  };

  handleGoHome = () => {
    window.location.href = '/';
  };

  render() {
    if (!this.state.hasError) return this.props.children;
    const msg = this.state.error?.message || String(this.state.error || '');
    return (
      <ErrorFallback
        msg={msg}
        onReload={this.handleReload}
        onGoHome={this.handleGoHome}
      />
    );
  }
}
